import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "../components/AdminLayout";
import { getAuthHeader } from "../context/AuthContext";
import axios from "axios";
import {
  DollarSign,
  ShoppingBag,
  Utensils,
  Grid3x3,
  Terminal,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchRestaurant();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRestaurant = async () => {
    try {
      const response = await axios.get(`${API}/restaurants`, {
        headers: getAuthHeader(),
      });
      if (response.data && response.data.length > 0) {
        setRestaurant(response.data[0]);
        localStorage.setItem("restaurant", response.data[0].name);
        const restaurant = response.data[0];

        localStorage.setItem(
          "restaurant_settings",
          JSON.stringify({
            id: restaurant.id,
            name: restaurant.name,
            country: restaurant.country,
            currency: restaurant.currency,
            currency_symbol: restaurant.currency_symbol,
            subdomain: restaurant.subdomain,
            locale: restaurant.locale || "en-US",
          })
        );
      }
    } catch (error) {
      console.error("Error fetching restaurant:", error);
      toast.error("Failed to load restaurant data");
    }
  };

const copyToClipboard = (url) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard
      .writeText(url)
      .then(() => {
        toast.success("URL copied successfully!");
      })
      .catch(() => {
        copyFallback(url);
      });
  } else {
    copyFallback(url);
  }

  function copyFallback(text) {
    const textArea = document.createElement("textarea");

    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";

    document.body.appendChild(textArea);

    textArea.select();

    try {
      document.execCommand("copy");
      toast.success("URL copied successfully!");
    } catch (err) {
      toast.error("Failed to copy URL");
    }

    document.body.removeChild(textArea);
  }
};
  const statCards = stats
    ? [
        {
          title: "Today Revenue",
          value: `$${stats.today_revenue}`,
          icon: DollarSign,
          gradient: "from-blue-500 to-cyan-500",
          testId: "stat-today-revenue",
        },
        {
          title: "Today Orders",
          value: stats.today_orders,
          icon: ShoppingBag,
          gradient: "from-purple-500 to-pink-500",
          testId: "stat-today-orders",
        },
        {
          title: "Available Tables",
          value: `${stats.available_tables}/${stats.total_tables}`,
          icon: Grid3x3,
          gradient: "from-green-500 to-emerald-500",
          testId: "stat-available-tables",
        },
        {
          title: "Menu Items",
          value: stats.total_menu_items,
          icon: Utensils,
          gradient: "from-orange-500 to-red-500",
          testId: "stat-menu-items",
        },
      ]
    : [];

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Dashboard</h1>
              {restaurant?.name && (
                <p className="text-gray-600">
                  Welcome to {restaurant.name} ORDERPOZ - Your restaurant management system
                </p>
              )}
          </div>
          <Button
            onClick={() => navigate("/pos")}
            className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-8 py-6 text-lg font-bold shadow-xl"
            data-testid="open-pos-button"
          >
            <Terminal size={24} className="mr-3" />
            Open POS Screen
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Loading...</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <Card
                  key={index}
                  className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300"
                  data-testid={card.testId}
                >
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">
                      {card.title}
                    </CardTitle>
                    <div
                      className={`p-2 rounded-lg bg-gradient-to-br ${card.gradient}`}
                    >
                      <Icon className="text-white" size={20} />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-gray-800">
                      {card.value}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

<div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-8">

  {/* ONLINE ORDERING */}
  {restaurant?.order_types?.online && (
    <Card className="border border-gray-200 shadow-sm rounded-2xl overflow-hidden bg-white hover:shadow-md transition-all duration-200">

      <div className="border-b bg-gradient-to-r from-red-50 to-orange-50 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-red-100 flex items-center justify-center text-xl">
            🛒
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-800">
              Online Ordering URL
            </h2>

            <p className="text-sm text-gray-500">
              Accept customer orders online
            </p>
          </div>
        </div>
      </div>

      <CardContent className="p-6">
        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
          Share this link with customers or add it to your website so they can
          place orders directly.
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={`${window.location.origin}/${restaurant.subdomain}`}
            readOnly
            onClick={(e) => e.target.select()}
            className="
              flex-1
              px-4 py-3
              border border-gray-300
              rounded-xl
              bg-gray-50
              text-gray-700
              font-mono
              text-sm
              focus:outline-none
              focus:ring-2
              focus:ring-red-300
            "
          />

          <Button
            onClick={() =>
              copyToClipboard(
                `${window.location.origin}/${restaurant.subdomain}`
              )
            }
            className="
              rounded-xl
              px-5
              h-[50px]
              bg-red-600
              hover:bg-red-700
              text-white
              font-medium
            "
          >
            Copy URL
          </Button>
        </div>

        <div className="mt-4 rounded-xl bg-red-50 border border-red-100 p-4">
          <p className="text-sm text-gray-700 leading-relaxed">
            💡 Customers can order directly without scanning QR codes.
            Orders will appear in your{" "}
            <span className="font-semibold">
              Online Orders
            </span>{" "}
            tab.
          </p>
        </div>
      </CardContent>
    </Card>
  )}

  {/* RESERVATION */}
  {restaurant?.order_types?.reservation && (
    <Card className="border border-gray-200 shadow-sm rounded-2xl overflow-hidden bg-white hover:shadow-md transition-all duration-200">

      <div className="border-b bg-gradient-to-r from-blue-50 to-cyan-50 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center text-xl">
            📅
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-800">
              Online Reservation URL
            </h2>

            <p className="text-sm text-gray-500">
              Allow customers to reserve tables
            </p>
          </div>
        </div>
      </div>

      <CardContent className="p-6">
        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
          Share this reservation link with customers for easy table bookings.
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={`${window.location.origin}/reserve/${restaurant.subdomain}`}
            readOnly
            onClick={(e) => e.target.select()}
            className="
              flex-1
              px-4 py-3
              border border-gray-300
              rounded-xl
              bg-gray-50
              text-gray-700
              font-mono
              text-sm
              focus:outline-none
              focus:ring-2
              focus:ring-blue-300
            "
          />

          <Button
            onClick={() =>
              copyToClipboard(
                `${window.location.origin}/reserve/${restaurant.subdomain}`
              )
            }
            className="
              rounded-xl
              px-5
              h-[50px]
              bg-blue-600
              hover:bg-blue-700
              text-white
              font-medium
            "
          >
            Copy URL
          </Button>
        </div>

        <div className="mt-4 rounded-xl bg-blue-50 border border-blue-100 p-4">
          <p className="text-sm text-gray-700 leading-relaxed">
            💡 Customers can reserve tables online.
            Reservations will appear in your{" "}
            <span className="font-semibold">
              Reservation
            </span>{" "}
            tab.
          </p>
        </div>
      </CardContent>
    </Card>
  )}

</div>
        {/* Quick Management Links */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold mb-4">Quick Management</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card
              className="cursor-pointer hover:shadow-lg transition-all border-2 hover:border-red-500"
              onClick={() => navigate("/menu-management")}
            >
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center mb-3">
                  <Utensils className="text-white" size={32} />
                </div>
                <h3 className="font-bold text-lg">Menu Management</h3>
                <p className="text-sm text-gray-600 mt-2">
                  Manage categories & menu items
                </p>
              </CardContent>
            </Card>

            <Card
              className="cursor-pointer hover:shadow-lg transition-all border-2 hover:border-blue-500"
              onClick={() => navigate("/table-management")}
            >
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mb-3">
                  <Grid3x3 className="text-white" size={32} />
                </div>
                <h3 className="font-bold text-lg">Table Management</h3>
                <p className="text-sm text-gray-600 mt-2">
                  Manage tables & sections
                </p>
              </CardContent>
            </Card>

            <Card
              className="cursor-pointer hover:shadow-lg transition-all border-2 hover:border-green-500"
              onClick={() => navigate("/orders")}
            >
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mb-3">
                  <ShoppingBag className="text-white" size={32} />
                </div>
                <h3 className="font-bold text-lg">Orders</h3>
                <p className="text-sm text-gray-600 mt-2">View all orders</p>
              </CardContent>
            </Card>

            <Card
              className="cursor-pointer hover:shadow-lg transition-all border-2 hover:border-purple-500"
              onClick={() => navigate("/categories")}
            >
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-3">
                  <Terminal className="text-white" size={32} />
                </div>
                <h3 className="font-bold text-lg">Categories</h3>
                <p className="text-sm text-gray-600 mt-2">
                  Manage categories & sub-categories
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {stats && (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>Quick Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Revenue</span>
                  <span className="font-bold text-lg">
                    ${stats.total_revenue}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Orders</span>
                  <span className="font-bold text-lg">
                    {stats.total_orders}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Occupied Tables</span>
                  <span className="font-bold text-lg">
                    {stats.occupied_tables}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
